import { STORAGE_KEYS, type StorageKey } from '@/constants/storageKeys'

// Re-export STORAGE_KEYS for convenience
export { STORAGE_KEYS }

/**
 * Centralized localStorage utility
 * Provides type-safe access to localStorage with error handling
 */

/**
 * Get item from localStorage
 * @param key - Storage key
 * @returns Stored value or null if not found
 */
export function getStorageItem(key: StorageKey): string | null {
  try {
    return localStorage.getItem(key)
  } catch (error) {
    console.error(`Error getting storage item "${key}":`, error)
    return null
  }
}

/**
 * Read session JWT with Angular parity: canonical key is `token` (STORAGE_KEYS.authToken).
 * Migrates legacy mistaken key `authToken` (literal string) used in a few components.
 */
export function getAuthTokenFromStorage(): string | null {
  const canonical = getStorageItem(STORAGE_KEYS.authToken)
  if (canonical) return canonical
  try {
    const legacy = localStorage.getItem('authToken')
    if (legacy) {
      setStorageItem(STORAGE_KEYS.authToken, legacy)
      localStorage.removeItem('authToken')
      return legacy
    }
  } catch {
    /* ignore */
  }
  return null
}

/**
 * Set item in localStorage
 * @param key - Storage key
 * @param value - Value to store
 */
export function setStorageItem(key: StorageKey, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch (error) {
    console.error(`Error setting storage item "${key}":`, error)
  }
}

/**
 * Remove item from localStorage
 * @param key - Storage key
 */
export function removeStorageItem(key: StorageKey): void {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Error removing storage item "${key}":`, error)
  }
}

/**
 * Get and parse JSON from localStorage
 * @param key - Storage key
 * @returns Parsed object or null if not found/invalid
 */
export function getStorageJSON<T>(key: StorageKey): T | null {
  try {
    const item = getStorageItem(key)
    if (!item) return null
    return JSON.parse(item) as T
  } catch (error) {
    console.error(`Error parsing storage JSON for "${key}":`, error)
    return null
  }
}

/**
 * Set JSON item in localStorage
 * @param key - Storage key
 * @param value - Value to stringify and store
 */
export function setStorageJSON<T>(key: StorageKey, value: T): void {
  try {
    const jsonString = JSON.stringify(value)
    setStorageItem(key, jsonString)
  } catch (error) {
    console.error(`Error stringifying storage JSON for "${key}":`, error)
  }
}

/**
 * Clear impersonation-related storage (Angular parity: clear on login/logout).
 * Stops "Stop Impersonate" from showing after logout and re-login as applicant.
 */
export function clearImpersonationStorage(): void {
  try {
    localStorage.removeItem('impersonate')
    localStorage.removeItem('impersonate_path')
    localStorage.removeItem('impersonatepath') // Angular key
    localStorage.removeItem('impersonated_applicant_id')
    localStorage.removeItem('admin_token')
    sessionStorage.removeItem('impersonate_process')
    sessionStorage.removeItem('impersonateprocess') // Angular key
  } catch (e) {
    console.error('Error clearing impersonation storage:', e)
  }
}

/** Angular `AuthService.isImpersonate()` parity: admin_token stashed OR `impersonate` flag set. */
export function isImpersonating(): boolean {
  try {
    return !!localStorage.getItem('admin_token') || localStorage.getItem('impersonate') === 'true'
  } catch {
    return false
  }
}

/**
 * Clear all auth-related storage items
 */
export function clearAuthStorage(): void {
  removeStorageItem(STORAGE_KEYS.authToken)
  removeStorageItem(STORAGE_KEYS.userRole)
  removeStorageItem(STORAGE_KEYS.applicantId)
  removeStorageItem(STORAGE_KEYS.fileAccessToken)
  // Angular parity: logout clears impersonation so re-login as applicant doesn't show "Stop Impersonate"
  clearImpersonationStorage()
}

/**
 * Clear all storage items
 */
export function clearAllStorage(): void {
  try {
    localStorage.clear()
  } catch (error) {
    console.error('Error clearing all storage:', error)
  }
}

